import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import ActionPackCard from '@/components/ActionPackCard';
import SectionAccordionCard from '@/components/SectionAccordionCard';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppSectionHeader,
  AppText,
} from '../../../ui-foundation';

type Template = {
  title: string;
  description: string;
  body: string;
};

function formatWorkMode(value: string) {
  if (value === 'self-employed') return 'self-employed';
  if (value === 'not-working') return 'not working';
  if (value === 'mix') return 'mixed';
  if (value === 'employed') return 'employed';
  return 'unset';
}

export default function WorkToolsTab() {
  const navigate = useNavigate();
  const { data, user } = useApp();
  const [copiedTitle, setCopiedTitle] = useState<string | null>(null);
  const [selectedTemplateTitle, setSelectedTemplateTitle] = useState<string | null>(null);
  const [openInvoiceCount, setOpenInvoiceCount] = useState(0);
  const [pendingDeadlineCount, setPendingDeadlineCount] = useState(0);
  const [openSections, setOpenSections] = useState({
    packs: false,
    drafts: false,
    routes: false,
  });
  const firstName = data.profile.firstName || 'there';
  const workType = data.profile.workType || data.profile.employmentType;

  const templates = useMemo<Template[]>(() => [
    {
      title: 'Redundancy / at-risk meeting',
      description: 'Use before or after a meeting when you need the facts in writing.',
      body: `Hi,\n\nThanks for meeting with me today. I want to make sure I have understood the position correctly.\n\nPlease can you confirm in writing:\n- the current status of the process\n- the reasons given\n- the timetable and next steps\n- any consultation or appeal options available to me\n- how notice, holiday pay, redundancy pay, or any other terms would be calculated if my role is removed\n\nI would also like a written summary of anything I need to do next.\n\nThanks,\n${firstName}`,
    },
    {
      title: 'Return-to-work / adjustments',
      description: 'Use when you need a clear, calm request for adjustments or phased return support.',
      body: `Hi,\n\nI am writing to ask for a conversation about returning to work safely and sustainably.\n\nI would like to discuss:\n- any reasonable adjustments that may help\n- whether a phased return is possible\n- what support is available during the transition back\n\nPlease let me know a suitable time to discuss this.\n\nThanks,\n${firstName}`,
    },
    {
      title: 'Formal grievance',
      description: 'Use when you need to raise a work problem formally and want the issue and next step written down.',
      body: `Hi,\n\nI am writing to raise a formal grievance about [issue].\n\nMy concern is:\n- what happened: [brief facts]\n- when it happened: [dates]\n- how it has affected me: [impact]\n\nPlease treat this under the formal grievance procedure. Please can you confirm the next steps, who will handle it, and when a meeting can be arranged.\n\nIf there is any information or evidence you would like me to send in advance, let me know.\n\nThanks,\n${firstName}`,
    },
    {
      title: 'Disciplinary response / prep',
      description: 'Use when allegations, paperwork, or meeting details are unclear and you need a calm written response.',
      body: `Hi,\n\nThank you for letting me know about the disciplinary meeting.\n\nTo prepare properly, please can you send me in writing:\n- the allegations or concerns being considered\n- any documents or evidence you plan to rely on\n- who will attend the meeting\n- whether I can be accompanied\n- the possible outcomes being considered\n\nIf I need time to review the documents before the meeting, please let me know how that can be arranged.\n\nThanks,\n${firstName}`,
    },
    {
      title: 'Appeal a work decision',
      description: 'Use when you want to challenge an outcome and need the appeal route confirmed in writing.',
      body: `Hi,\n\nI am writing to appeal the outcome communicated to me on [date].\n\nI do not agree with the decision and I would like the appeal process confirmed in writing. Please can you let me know:\n- the appeal deadline\n- who will hear the appeal\n- what information or evidence you would like me to send\n- whether there will be an appeal meeting\n\nI would also like a copy of any notes or documents relied on in reaching the original decision.\n\nThanks,\n${firstName}`,
    },
    {
      title: 'Ask for written reasons / outcome',
      description: 'Use when a meeting ended vaguely and you need the decision, reasons, and next steps clearly in writing.',
      body: `Hi,\n\nThanks for meeting with me today.\n\nPlease can you confirm in writing:\n- the decision or current position\n- the reasons for it\n- any evidence or criteria relied on\n- any next steps or timescales\n- whether there is a review or appeal route available\n\nI want to make sure I understand the position correctly and do not miss any deadlines.\n\nThanks,\n${firstName}`,
    },
    {
      title: 'Settlement agreement discussion',
      description: 'Use when an exit or settlement conversation has started and you need the process and terms made clear.',
      body: `Hi,\n\nFollowing our conversation, please can you confirm in writing whether settlement is being proposed and, if so:\n- the headline terms being discussed\n- whether I am expected to sign anything at this stage\n- what timescale applies\n- whether I should seek independent advice before responding\n\nI would like the position to be clear before I take any next step.\n\nThanks,\n${firstName}`,
    },
    {
      title: 'Late payment chase',
      description: 'Use when an invoice is overdue and you need to move it forward without rewriting from scratch.',
      body: `Hi,\n\nI am following up on invoice [invoice number], which was due on [due date].\n\nPlease can you confirm when payment will be made. If there is an issue or something missing from my side, let me know and I will sort it quickly.\n\nThanks,\n${firstName}`,
    },
    {
      title: 'Flexible working / caring request',
      description: 'Use when work needs to reflect real caring responsibilities at home.',
      body: `Hi,\n\nI would like to discuss a flexible working arrangement to help me manage my work alongside caring responsibilities.\n\nI would like to explore what options may be possible, for example changes to hours, start/finish times, or working pattern.\n\nPlease let me know a suitable time to talk this through.\n\nThanks,\n${firstName}`,
    },
  ], [firstName]);

  useEffect(() => {
    if (!user) return;

    async function fetchSummary() {
      const [invoiceResult, hmrcDeadlineResult, employmentDeadlineResult] = await Promise.all([
        supabase
          .from('work_invoices' as any)
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .neq('status', 'paid'),
        supabase
          .from('hmrc_deadlines')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('completed', false),
        supabase
          .from('work_deadlines' as any)
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('completed', false),
      ]);

      setOpenInvoiceCount(invoiceResult.count || 0);
      setPendingDeadlineCount((hmrcDeadlineResult.count || 0) + (employmentDeadlineResult.count || 0));
    }

    void fetchSummary();
    const handleRefresh = () => {
      void fetchSummary();
    };
    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [user]);

  const copyTemplate = async (template: Template) => {
    await navigator.clipboard.writeText(template.body);
    setCopiedTitle(template.title);
    toast.success('Draft copied to clipboard.');
    setTimeout(() => setCopiedTitle(null), 2000);
  };

  const templateByTitle = useMemo(
    () => Object.fromEntries(templates.map((template) => [template.title, template])),
    [templates],
  );
  const selectedTemplate = selectedTemplateTitle ? templateByTitle[selectedTemplateTitle] : null;

  const copyByTitle = async (title: string) => {
    const template = templateByTitle[title];
    if (!template) return;
    await copyTemplate(template);
  };

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  return (
    <div className="space-y-5">
      <SectionAccordionCard
        emoji="🧰"
        title="Quick packs"
        subtitle="Use the pack that matches the moment instead of digging through one long drafting page."
        open={openSections.packs}
        onToggle={() => toggleSection('packs')}
      >
        <div className="space-y-5">
          <AppCard gap="$md">
            <AppSectionHeader
              title="Work drafting tools"
              subtitle="Keep the wording simple. These templates are here to help you get a draft out fast for formal work moments, then use the guides if the situation needs more depth."
            />
            <div className="mt-4 grid grid-cols-3 gap-3">
              <MetricCard label="Mode" value={formatWorkMode(workType)} />
              <MetricCard label="Open invoices" value={String(openInvoiceCount)} />
              <MetricCard label="Deadlines" value={String(pendingDeadlineCount)} />
            </div>
          </AppCard>

          <div className="grid gap-4 lg:grid-cols-2">
            {(workType === 'employed' || workType === 'mix' || !workType) && (
              <ActionPackCard
                icon="⚖️"
                eyebrow="At-Risk Pack"
                title="Move quickly when work feels shaky"
                description="When you think redundancy, capability, or a difficult process may be starting, get the facts and keep them written down early."
                steps={[
                  'Ask for the process, reasons, and timescale in writing.',
                  'Keep one short record after each meeting instead of relying on memory.',
                  'Use the rights guides when the situation moves from unclear to formal.',
                ]}
                actions={[
                  { label: 'Copy redundancy draft', onClick: () => void copyByTitle('Redundancy / at-risk meeting'), primary: true },
                  { label: 'Open rights', onClick: () => navigate('/work?tab=rights') },
                ]}
              />
            )}

            {(workType === 'employed' || workType === 'mix' || workType === 'not-working' || !workType) && (
              <ActionPackCard
                icon="🪴"
                eyebrow="Return-to-Work Pack"
                title="Use short written requests for adjustments and phased return"
                description="Do not wait until a hard return becomes a crisis. Get the request written down and keep the next conversation concrete."
                steps={[
                  'State that you want a safe, sustainable return rather than vaguely “coping”.',
                  'Ask directly about adjustments, phased return, or flexibility.',
                  'Keep the follow-up written so the next step is visible.',
                ]}
                actions={[
                  { label: 'Copy return-to-work draft', onClick: () => void copyByTitle('Return-to-work / adjustments'), primary: true },
                  { label: 'Open support hub', onClick: () => navigate('/guides?section=work') },
                ]}
              />
            )}

            {(workType === 'self-employed' || workType === 'mix') && (
              <ActionPackCard
                icon="💸"
                eyebrow="Self-Employed Pack"
                title="Keep invoices and HMRC admin in the same work loop"
                description="The strongest self-employed flow is one home for money in, money outstanding, and deadlines that can turn expensive."
                steps={[
                  'Track invoices instead of chasing from memory.',
                  'Keep HMRC deadlines visible before they become urgent.',
                  'Use a short payment chase script as soon as the due date slips.',
                ]}
                actions={[
                  { label: 'Open self-employed', onClick: () => navigate('/work?tab=self-employed'), primary: true },
                  { label: 'Copy payment chase', onClick: () => void copyByTitle('Late payment chase') },
                ]}
              />
            )}

            {(workType === 'employed' || workType === 'mix' || !workType) && (
              <ActionPackCard
                icon="🕰️"
                eyebrow="Care & Flexibility"
                title="Keep caring pressure visible before it becomes a work problem"
                description="Use one calm written request rather than repeatedly improvising explanations when work and care collide."
                steps={[
                  'State the practical problem you are trying to solve, not your whole life story.',
                  'Ask for the specific flexibility or pattern change that would help.',
                  'Confirm the next discussion or response date in writing.',
                ]}
                actions={[
                  { label: 'Copy flexible working draft', onClick: () => void copyByTitle('Flexible working / caring request'), primary: true },
                  { label: 'Open rights', onClick: () => navigate('/work?tab=rights') },
                ]}
              />
            )}

            {(workType === 'employed' || workType === 'mix' || !workType) && (
              <ActionPackCard
                icon="📝"
                eyebrow="Formal Process Pack"
                title="Get the formal steps in writing before they move on without you"
                description="If grievance, disciplinary, appeal, or outcome wording feels vague, write first and keep the next step visible."
                steps={[
                  'Ask for allegations, reasons, evidence, and deadlines in writing.',
                  'Keep one short timeline of meetings and messages instead of trusting memory.',
                  'Use the rights tab when the issue overlaps with pay, leave, discrimination, or whistleblowing.',
                ]}
                actions={[
                  { label: 'Copy grievance draft', onClick: () => void copyByTitle('Formal grievance'), primary: true },
                  { label: 'Copy appeal draft', onClick: () => void copyByTitle('Appeal a work decision') },
                ]}
              />
            )}
          </div>
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="✍️"
        title="Drafts and scripts"
        subtitle="Open just the written templates when you need wording fast."
        open={openSections.drafts}
        onToggle={() => toggleSection('drafts')}
      >
        <div className="space-y-4">
          <AppCard backgroundColor="$background" gap="$sm">
            <AppSectionHeader
              title="Script helper pack"
              subtitle="Pick one draft to preview, or copy it straight away. The full wording stays tucked away until it is useful."
            />
          </AppCard>

          <div className="grid gap-3 lg:grid-cols-2">
            {templates.map((template) => (
              <AppCard
                key={template.title}
                backgroundColor={selectedTemplateTitle === template.title ? '$accentTint' : '$background'}
                borderColor={selectedTemplateTitle === template.title ? '$accent' : '$borderColor'}
                gap="$sm"
              >
                <AppCardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <AppText fontSize="$md" fontWeight="700">
                        {template.title}
                      </AppText>
                      <AppText color="$colorMuted" marginTop="$xs">
                        {template.description}
                      </AppText>
                    </div>
                  </div>
                </AppCardHeader>
                <AppCardBody>
                  <AppButtonRow>
                    <AppButton
                      type="button"
                      tone={selectedTemplateTitle === template.title ? 'primary' : 'outline'}
                      size="sm"
                      onPress={() => setSelectedTemplateTitle(template.title)}
                    >
                      Preview
                    </AppButton>
                    <AppButton
                      type="button"
                      tone="outline"
                      size="sm"
                      onPress={() => void copyTemplate(template)}
                    >
                      <Copy size={12} />
                      {copiedTitle === template.title ? 'Copied' : 'Copy'}
                    </AppButton>
                  </AppButtonRow>
                </AppCardBody>
              </AppCard>
            ))}
          </div>

          {selectedTemplate ? (
            <AppCard gap="$sm">
              <AppCardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <AppText fontSize="$md" fontWeight="700">
                      {selectedTemplate.title}
                    </AppText>
                    <AppText color="$colorMuted" marginTop="$xs">
                      Preview the wording, then copy it when you are ready.
                    </AppText>
                  </div>
                  <AppButton
                    type="button"
                    tone="outline"
                    size="sm"
                    onPress={() => void copyTemplate(selectedTemplate)}
                  >
                    <Copy size={12} />
                    {copiedTitle === selectedTemplate.title ? 'Copied' : 'Copy'}
                  </AppButton>
                </div>
              </AppCardHeader>
              <AppCardBody>
                <pre className="whitespace-pre-wrap rounded-xl border border-border bg-background p-3 text-xs text-foreground leading-relaxed font-sans">
                  {selectedTemplate.body}
                </pre>
              </AppCardBody>
            </AppCard>
          ) : (
            <AppCard backgroundColor="$background" gap="$sm">
              <AppText color="$colorMuted">
                No script preview open. Choose Preview on the one you need and Adulting will keep the rest out of the way.
              </AppText>
            </AppCard>
          )}
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🗂️"
        title="Next routes"
        subtitle="Move from quick drafting into the deeper guides when the situation gets bigger."
        open={openSections.routes}
        onToggle={() => toggleSection('routes')}
      >
        <AppCard gap="$sm">
          <AppSectionHeader
            title="Work guides and playbooks"
            subtitle="Open the guides for redundancy, self-employment admin, and return-to-work situations when the simple template is not enough."
            action={<ArrowRight size={16} className="shrink-0 text-muted-foreground" />}
          />
          <AppCardBody>
            <AppButtonRow>
              <AppButton type="button" tone="outline" onPress={() => navigate('/guides?section=work')}>
                Open support hub
              </AppButton>
            </AppButtonRow>
          </AppCardBody>
        </AppCard>
      </SectionAccordionCard>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <AppCard backgroundColor="$background" px="$sm" py="$sm" gap="$xs">
      <AppText fontSize="$xs" fontWeight="600" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">
        {label}
      </AppText>
      <AppText fontSize="$md" fontWeight="700" textTransform="capitalize">
        {value}
      </AppText>
    </AppCard>
  );
}
